/**
 * CORE-F — Skill heatmap · vocabulary
 *
 * Constitutional authority:
 *   `docs/constitution/07 - Feature Library/02 - Core/
 *    C-21 Proficiency & Capability Authority/CORE-F - Skill heatmap.md`
 *
 * CORE-F is a PRESENTATION authority. It owns how capability is rendered,
 * which dimensions are shown, and how confidence and absence are
 * communicated. **It owns no value.** Every number it displays belongs to
 * another capability, and this module's job is to carry each one across
 * without flattening it into something it is not.
 *
 * The state model is the whole point
 * ----------------------------------
 * The original implementation read `proficiency?.proficiency_score || 0`.
 * A category the technician had never worked rendered as `0`, in the
 * bottom colour band, labelled "Novice" — the system telling somebody they
 * are bad at something they have simply never done. A failed fetch did the
 * same thing across every category at once.
 *
 * So absence, failure and zero are three different states here, and no
 * code path can collapse them: `DimensionState` is a discriminated union
 * with no numeric field on the branches that have no number. There is
 * nothing to read a zero out of.
 *
 * Provenance separation is also structural. Each dimension carries its
 * owning capability and its own unit, and the render layer is forbidden —
 * by the shape of the data, not by a comment — from pooling them into one
 * colour scale. Readiness is not achievement. Diagnostic is not repair.
 * Volume is not proficiency. Efficiency is not proficiency.
 *
 * Purity: type-only module. No I/O, no clock, no React.
 */

// =============================================================================
// Dimension identity and provenance
// =============================================================================

/**
 * The dimensions CORE-F may present.
 *
 * Each belongs to a DIFFERENT owning capability and carries a different
 * evidence class. The constitution names this surface as "the single
 * highest-risk presentation surface in Core for the provenance doctrine",
 * which is why the owner travels with the value rather than being implied
 * by where it happens to be rendered.
 */
export type HeatmapDimensionKey =
  | 'proficiency'
  | 'diagnostic'
  | 'readiness'
  | 'efficiency'
  | 'volume';

export const HEATMAP_DIMENSIONS = Object.freeze([
  'proficiency',
  'diagnostic',
  'readiness',
  'efficiency',
  'volume',
] as const) satisfies ReadonlyArray<HeatmapDimensionKey>;

/**
 * The capability that owns each VALUE. CORE-F owns none of them.
 *
 * Efficiency is the one that is easy to get wrong, and this record got it
 * wrong on the first pass. CORE-G owns the efficiency value; CORE-AN owns
 * the mechanism that updates it, and says so in its own record:
 * *"CORE-AN does not own efficiency. It changes CORE-G's value. It is not
 * a second efficiency authority and must never become one."* Naming
 * CORE-AN here would have made this surface the place that quietly created
 * the second authority CORE-AN forbids.
 */
export const HEATMAP_DIMENSION_OWNER: Readonly<
  Record<HeatmapDimensionKey, string>
> = Object.freeze({
  proficiency: 'CORE-E',
  diagnostic: 'CORE-I',
  readiness: 'CORE-AI',
  efficiency: 'CORE-G',
  volume: 'CORE-J',
});

/**
 * The capability whose evidence mechanism MAINTAINS each value, where that
 * differs from the owner. Consuming a value through a learning mechanism is
 * not the same as that mechanism owning it.
 */
export const HEATMAP_DIMENSION_EVIDENCE_SOURCE: Readonly<
  Partial<Record<HeatmapDimensionKey, string>>
> = Object.freeze({
  efficiency: 'CORE-AN',
  proficiency: 'CORE-AM',
});

/**
 * What KIND of claim each dimension makes.
 *
 * Rendered to the reader, because the difference is the point: a person
 * who has completed training in a category has not thereby demonstrated
 * repair capability in it, and a screen that implies otherwise is making a
 * claim the evidence does not support.
 */
export const HEATMAP_DIMENSION_CLAIM: Readonly<
  Record<HeatmapDimensionKey, string>
> = Object.freeze({
  proficiency: 'Demonstrated repair capability',
  diagnostic: 'Diagnostic capability — not the same as repair',
  readiness: 'Training preparation — not demonstrated work',
  efficiency: 'Speed against expectation — not capability',
  volume: 'How much work is behind the assessment',
});

/** The unit each value is expressed in, so no two are silently compared. */
export type HeatmapUnit = 'score_0_100' | 'ratio_0_1' | 'percent' | 'count';

export const HEATMAP_DIMENSION_UNIT: Readonly<
  Record<HeatmapDimensionKey, HeatmapUnit>
> = Object.freeze({
  proficiency: 'score_0_100',
  diagnostic: 'score_0_100',
  readiness: 'score_0_100',
  efficiency: 'percent',
  volume: 'count',
});

// =============================================================================
// The state model
// =============================================================================

/**
 * Why an authoritative source could not be read.
 *
 *   - `'read_failed'`         — the query errored. We do not know anything.
 *   - `'not_linked'`          — this Passport is not attached to the record
 *                               the source is keyed by, so there is no key
 *                               to look with. Not evidence of absence.
 *   - `'unsupported'`         — the source that supplied this figure does
 *                               not express this dimension at all.
 *   - `'insufficient_basis'`  — rows exist, but they do not carry what is
 *                               needed to compute the figure without
 *                               inventing part of it.
 */
export type HeatmapUnavailableReason =
  | 'read_failed'
  /**
   * Core-100 R4 wave 3: the server states that this viewer's read basis does
   * not cover the source (passport_section_read_access). RLS would have
   * answered with zero rows; that is a fact about the VIEWER, never "no
   * evidence" about the person.
   */
  | 'not_permitted'
  | 'not_linked'
  | 'unsupported'
  | 'insufficient_basis';

/**
 * One dimension's state for one category.
 *
 * A discriminated union rather than `number | null`, because the whole
 * class of defect this capability suffered came from a nullable number
 * meeting a `|| 0`. `loading`, `unavailable` and `no_evidence` carry NO
 * value field, so there is no number for a fallback to reach for.
 *
 *   - `loading`     — we do not know yet.
 *   - `unavailable` — the source could not be read. **Failure is not a fact.**
 *   - `no_evidence` — the read succeeded and this person has no evidence
 *                     here. **Unknown is not zero.**
 *   - `known`       — a real value from the owning capability. `value` MAY
 *                     legitimately be `0`, and that is a different claim
 *                     from `no_evidence`.
 */
export type DimensionState =
  | { status: 'loading' }
  | { status: 'unavailable'; reason: HeatmapUnavailableReason }
  | { status: 'no_evidence' }
  | { status: 'known'; value: number };

export function isKnown(
  s: DimensionState,
): s is { status: 'known'; value: number } {
  return s.status === 'known';
}

// =============================================================================
// The two proficiency stores
// =============================================================================

/**
 * WHERE a proficiency figure is stored. Deliberately not a claim about how
 * it was produced.
 *
 * CORE-E keeps two divergent stores today, by documented design rather than
 * drift, and **CORE-F must not pretend that divergence is solved.**
 *
 *   - `'portable_record'`       — `technician_proficiencies`. Per portable
 *                                 profile × category, with columns for
 *                                 confidence, sample size and a timestamp.
 *                                 It is the identity representation that
 *                                 Passport and Connect read.
 *   - `'scheduling_authority'`  — `technicians.proficiencies`. Shop-scoped,
 *                                 authoritative for **scheduling**, with no
 *                                 confidence and no sample size columns at
 *                                 all.
 *
 * These names are LOCATIONS, and that is the whole point of this revision.
 *
 * An earlier version of this module called the portable store "derived from
 * completed work". That is false for some rows. The DMC-F sync trigger
 * (`20260509120000_dmc_f_passport_sync_trigger.sql`) mirrors
 * `technicians.proficiencies` straight into `technician_proficiencies`,
 * writing a **constant** `confidence_score = 0.5`, no `sample_size` at all,
 * and overwriting `confidence_score` on conflict — so a shop editing a
 * technician can replace a genuinely derived confidence with that constant.
 * A row sitting in the portable table may therefore be a mirror of a
 * manually typed scheduling number.
 *
 * Nor may the scheduling store be described as "entered by the shop": its
 * value may be a manual baseline, a penalty applied by
 * `comebackProficiencyAdjustment`, or a number carrying both influences.
 *
 * CORE-E-SEC-001 states the underlying reason for both prohibitions:
 * *"Manual and derived values are indistinguishable at rest… no provenance
 * field on either store."* CORE-F cannot establish what it is not told, and
 * inventing provenance is the exact failure this surface is most exposed
 * to — in either direction.
 */
export type ProficiencyProvenance = 'portable_record' | 'scheduling_authority';

export const PROVENANCE_LABEL: Readonly<
  Record<ProficiencyProvenance, string>
> = Object.freeze({
  portable_record: 'Passport proficiency record',
  scheduling_authority: 'Current shop scheduling value',
});

export const PROVENANCE_EXPLANATION: Readonly<
  Record<ProficiencyProvenance, string>
> = Object.freeze({
  portable_record:
    'The proficiency record carried with this Passport. It is what Passport and Connect read, and it is the figure shown here.',
  scheduling_authority:
    'The value the current shop uses for scheduling. It is shown as context only, it carries no confidence or sample size, and it is not treated here as demonstrated capability.',
});

/**
 * The limitation that applies to the PORTABLE record, stated wherever the
 * portable figure is explained.
 *
 * A reader is entitled to know that the number in front of them does not come
 * with a record of where it came from — particularly on a screen whose whole
 * subject is what they can demonstrably do.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHY THIS NO LONGER SAYS "BOTH STORES"  (CORE-E, 2026-09-08)
 * ───────────────────────────────────────────────────────────────────────────
 * It used to, and that was true when written. It is now false of the
 * scheduling side: `20261122120000` gave CORE-E `technician_category_authority`
 * — a baseline WITH its class, an evidence count, a confidence, a defect
 * adjustment — and `20261126120000` gave `technicians.proficiency_baseline_class`
 * a recorded source at the two writers that actually know one. The scheduling
 * store's provenance is explained by `CategoryAuthorityExplanation` now.
 *
 * `technician_proficiencies` still carries no source column, so the claim is
 * narrowed to the store it is still true of rather than deleted. Stating it of
 * the scheduling store would now be the overclaim in the opposite direction:
 * telling a person nothing is known about a number whose source IS recorded.
 */
export const PROVENANCE_LIMITATION =
  'For this value the portable record does not identify whether it originated from a manual baseline, a synced shop value, or later derived operational evidence: its source was not recorded when it was written.';

/**
 * Core-100 R4 (CORE-E / CORE-F §Provenance): the portable record now carries its class at rest
 * (`technician_proficiencies.provenance_class`, R2-49; one writer since 20270206011900). "A manually-entered 70% and
 * an evidence-derived 70% must not render identically": each tile states its class in words (never colour alone),
 * and the "cannot be told" limitation above is said ONLY of a value whose class is genuinely unrecorded.
 */
export type PortableProvenanceClass = 'demonstrated' | 'manager_asserted' | 'passport_carried' | 'unrecorded';

export function portableProvenanceClassOf(raw: string | null | undefined): PortableProvenanceClass {
  return raw === 'demonstrated' || raw === 'manager_asserted' || raw === 'passport_carried' ? raw : 'unrecorded';
}

export const PORTABLE_CLASS_LABEL: Record<PortableProvenanceClass, string> = {
  demonstrated: 'Demonstrated in completed work',
  manager_asserted: 'Manager-entered baseline (not yet demonstrated)',
  passport_carried: 'Carried from a previous shop (not yet demonstrated here)',
  unrecorded: 'Source not recorded',
};

/** Short tile marker, so the class is visible on sight, not only in the detail. */
export const PORTABLE_CLASS_MARK: Record<PortableProvenanceClass, string> = {
  demonstrated: 'Demonstrated',
  manager_asserted: 'Baseline',
  passport_carried: 'Carried',
  unrecorded: 'Source unrecorded',
};

/**
 * How the two stores relate for one category.
 *
 *   - `'portable_only'`    — only the portable record exists.
 *   - `'scheduling_only'`  — only the current shop's scheduling value.
 *   - `'agree'`            — both exist and match.
 *   - `'differ'`           — both exist and disagree. **Disclosed, never
 *                            resolved.** CORE-E-GAP-001 means positive
 *                            evidence never reaches the scheduling store,
 *                            so a technician who has improved will
 *                            routinely be scheduled on the older number.
 *                            That divergence is a fact about the system,
 *                            and hiding it would be choosing one authority
 *                            invisibly on the reader's behalf.
 *   - `'none'`             — neither exists.
 */
export type ProficiencyDivergence =
  | 'none'
  | 'portable_only'
  | 'scheduling_only'
  | 'agree'
  | 'differ';

/**
 * How close two proficiency figures must be to count as agreeing.
 *
 * Both stores hold whole-number-ish scores; this tolerance absorbs float
 * representation only, and is deliberately far too small to paper over a
 * real disagreement.
 */
export const PROFICIENCY_AGREEMENT_TOLERANCE = 0.5;

// =============================================================================
// Evidence strength
// =============================================================================

/**
 * How much work stands behind a proficiency claim.
 *
 * Its own axis, deliberately. The constitution forbids presenting "a single
 * successful job as mastery" and "a low sample count as certainty", and a
 * value cannot carry that on its own — 90% from two jobs and 90% from two
 * hundred are the same number and completely different claims.
 *
 *   - `'none'`     — no completed work behind it.
 *   - `'sparse'`   — below the shop's stated adequacy threshold.
 *   - `'adequate'` — enough to be worth reading.
 *   - `'strong'`   — well supported.
 *   - `'unknown'`  — the sample count itself could not be established.
 *                    Distinct from `'none'`, and the NORMAL state for a
 *                    scheduling-authority value, which carries no sample
 *                    size at all (CORE-E-GAP-004).
 */
export type EvidenceStrength =
  | 'unknown'
  | 'none'
  | 'sparse'
  | 'adequate'
  | 'strong';

/**
 * Sample thresholds.
 *
 * `SPARSE_BELOW` preserves the existing product behaviour (`sampleSize < 3`
 * drove the old opacity rule) rather than inventing a new confidence
 * policy, which would be CORE-E's and CORE-AM's to set, not CORE-F's. The
 * higher band is a presentation distinction only — it changes how confident
 * the screen LOOKS, never what any value IS.
 */
export const EVIDENCE_SPARSE_BELOW = 3;
export const EVIDENCE_STRONG_AT_OR_ABOVE = 10;

// =============================================================================
// Recency
// =============================================================================

/**
 * How recently a category produced evidence.
 *
 * CORE-F reports recency; it never decays a value for it. The constitution
 * is explicit that CORE-F must not alter proficiency because evidence is
 * old unless the owning capability already provides that derivation — and
 * CORE-E does not. **Old evidence remains evidence.**
 *
 * `'unknown'` exists so a missing timestamp cannot become `'older'`, which
 * would be the recency version of turning unknown into zero.
 */
export type RecencyBand = 'unknown' | 'recent' | 'aging' | 'older';

export const RECENCY_RECENT_WITHIN_DAYS = 30;
export const RECENCY_AGING_WITHIN_DAYS = 90;

// =============================================================================
// Proficiency banding
// =============================================================================

/**
 * The demonstrated-capability bands.
 *
 * `'unknown'` is a first-class band, not the bottom of the scale. That
 * distinction is the constitutional prohibition this capability most
 * clearly violated: a never-worked category previously rendered in the
 * same band as genuinely poor performance.
 */
export type ProficiencyBand =
  | 'unknown'
  | 'novice'
  | 'developing'
  | 'capable'
  | 'proficient'
  | 'expert';

export const PROFICIENCY_BAND_ORDER = Object.freeze([
  'novice',
  'developing',
  'capable',
  'proficient',
  'expert',
] as const);

// =============================================================================
// The rendered cell
// =============================================================================

/**
 * Everything CORE-F knows about one category, ready to render.
 *
 * Note what is absent: no single composite score, and no field into which
 * several dimensions have been averaged. Each dimension keeps its own
 * state, its own owner and its own unit all the way to the screen. The two
 * proficiency stores likewise stay side by side — `portable` and
 * `scheduling` are never added, averaged, or preferred silently.
 */
export interface HeatmapCell {
  category: string;
  displayName: string;

  /**
   * The tile figure. **Always the portable Passport record**, and never
   * anything else.
   *
   * CORE-F's constitutional subject is demonstrated capability, and the
   * capability bands — Novice through Expert — are statements about it. A
   * previous revision fell back to the shop's scheduling value when the
   * portable record was absent, which put a number of unresolved provenance
   * straight onto that ramp: a scheduling-only 85 rendered "Expert", and a
   * scheduling-only 10 rendered "Novice", about a person whose demonstrated
   * record the system had never seen. The scheduling value is still shown,
   * as `scheduling` below, but it is context — not a rung on this scale.
   */
  proficiency: DimensionState;
  /** Banded from `proficiency` alone, so only the portable record is banded. */
  band: ProficiencyBand;

  /** `technician_proficiencies` — same value as `proficiency`, named. */
  portable: DimensionState;
  /**
   * Core-100 R4: the portable value's provenance class as stored. `null` only when the portable record has no row
   * for this category (nothing to classify).
   */
  portableClass: PortableProvenanceClass | null;
  /** `technicians.proficiencies` — the current shop's operational value. */
  scheduling: DimensionState;
  /** How the two relate. Disclosed rather than resolved. */
  divergence: ProficiencyDivergence;

  /**
   * Confidence as RECORDED on the portable row, 0..1. Displayed exactly as
   * stored and never recomputed — and never described as a derived
   * confidence, since the sync trigger writes a constant `0.5` and CORE-E
   * stores nothing that would let us tell the two apart.
   */
  confidence: DimensionState;
  /** Sample size as recorded on the portable row. */
  sampleSize: DimensionState;
  /** The coarse band over `sampleSize`, offered alongside the count. */
  evidence: EvidenceStrength;

  /** CORE-AM. Derived only from a timestamp the owner already wrote. */
  recency: RecencyBand;
  recencyDate: string | null;

  /** CORE-I — separate from repair capability. */
  diagnostic: DimensionState;
  /** CORE-AI — preparation, not achievement. */
  readiness: DimensionState;
  /** CORE-G's value, maintained by CORE-AN — speed, not capability. */
  efficiency: DimensionState;
  /**
   * True when some completed work in this category could not contribute to
   * the efficiency figure. What is shown is then a partial roll-up and must
   * be labelled as one rather than presented as the category total.
   */
  efficiencyPartial: boolean;
  /** CORE-J — completed-work volume. */
  volume: DimensionState;

  /**
   * True when at least one dimension carries real evidence. Drives whether
   * the cell reads as "we know something here" at all.
   */
  hasAnyEvidence: boolean;
}

/** Per-source load state, so one failure cannot erase the others. */
export type SourceStatus = 'loading' | 'ok' | 'error' | 'not_linked' | 'not_permitted';

export interface HeatmapSourceStates {
  /** `technician_proficiencies` — the portable record. */
  proficiency: SourceStatus;
  /**
   * `technicians.proficiencies` — the current shop's scheduling map.
   * Independent of the portable read: either may be present without the
   * other, and **neither may erase the other**.
   */
  scheduling: SourceStatus;
  diagnostic: SourceStatus;
  readiness: SourceStatus;
  /** Efficiency and volume share `technician_job_learning_stats`. */
  jobStats: SourceStatus;
}
